import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/Brand";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SECTIONS = [
  {
    id: "gdpr",
    title: "GDPR e Codice Privacy",
    ref: "Reg. UE 2016/679 · D.Lgs. 196/2003 mod. D.Lgs. 101/2018",
    body: [
      "Il Regolamento (UE) 2016/679 (GDPR) disciplina il trattamento dei dati personali delle persone fisiche ed è applicabile dal 25 maggio 2018. In Italia è integrato dal Codice in materia di protezione dei dati personali (D.Lgs. 196/2003), modificato dal D.Lgs. 101/2018.",
      "Il condominio tratta dati personali di condòmini, conduttori, fornitori e dipendenti: deve quindi rispettare i principi di liceità, correttezza, trasparenza, minimizzazione e sicurezza (art. 5 GDPR) e informare gli interessati con un'informativa chiara (artt. 13 e 14 GDPR).",
    ],
  },
  {
    id: "ruoli",
    title: "Chi è il titolare in condominio",
    ref: "Artt. 4, 26 e 28 GDPR · artt. 1129-1131 c.c.",
    body: [
      "Il condominio, rappresentato dall'amministratore, è titolare dei trattamenti necessari alla gestione delle parti comuni. I condòmini, che determinano insieme finalità e mezzi del trattamento, sono contitolari: per questo è opportuno un accordo di contitolarità ai sensi dell'art. 26 GDPR.",
      "L'amministratore tratta i dati per conto del condominio come responsabile del trattamento (art. 28 GDPR) ed è titolare autonomo per i trattamenti relativi alla propria attività professionale e al proprio studio. Il Garante è tornato sul tema con un documento di indirizzo dedicato all'ambito condominiale nel 2025.",
    ],
  },
  {
    id: "informative",
    title: "Informative ai condòmini",
    ref: "Artt. 12-14 GDPR",
    body: [
      "L'informativa deve indicare titolare, finalità e base giuridica, destinatari, tempi di conservazione e diritti dell'interessato. Deve essere resa in forma concisa, trasparente e facilmente accessibile.",
      "La pubblicazione online, con accesso tramite il codice fiscale del condominio, consente a tutti gli interessati di consultare sempre la versione aggiornata.",
    ],
  },
  {
    id: "videosorveglianza",
    title: "Videosorveglianza nelle parti comuni",
    ref: "Art. 1122-ter c.c. · Provv. Garante 8 aprile 2010 · Linee guida EDPB 3/2019",
    body: [
      "L'installazione di impianti di videosorveglianza sulle parti comuni è deliberata dall'assemblea con la maggioranza prevista dall'art. 1136, secondo comma, c.c.",
      "Le aree riprese devono essere segnalate con cartelli visibili prima del raggio d'azione delle telecamere; le immagini vanno conservate per tempi brevi (di norma 24-48 ore, salvo esigenze particolari) e consultate solo da persone autorizzate.",
    ],
  },
  {
    id: "formazione",
    title: "Formazione degli amministratori",
    ref: "Art. 71-bis disp. att. c.c. · D.M. 13 agosto 2014, n. 140",
    body: [
      "Per svolgere l'incarico, l'amministratore di condominio deve aver frequentato un corso di formazione iniziale e svolgere attività di formazione periodica in materia di amministrazione condominiale.",
      "Il D.M. 140/2014 stabilisce che la formazione periodica abbia durata di almeno 15 ore annue. La protezione dei dati personali e la sicurezza sono tra i temi di maggior rilievo per l'attività professionale.",
    ],
  },
  {
    id: "acqua",
    title: "Qualità dell'acqua negli impianti condominiali",
    ref: "D.Lgs. 23 febbraio 2023, n. 18 · Linee guida legionellosi 2015",
    body: [
      "Il D.Lgs. 18/2023, in attuazione della Direttiva (UE) 2020/2184, disciplina la qualità delle acque destinate al consumo umano e introduce un approccio basato sulla valutazione del rischio anche per i sistemi di distribuzione idrica interni agli edifici.",
      "A valle del punto di consegna, la responsabilità dell'impianto idrico condominiale ricade sul condominio. Controlli periodici e la prevenzione della legionella negli impianti di acqua calda sanitaria tutelano la salute dei residenti.",
    ],
  },
  {
    id: "sicurezza",
    title: "Sicurezza sul lavoro e DUVRI",
    ref: "D.Lgs. 9 aprile 2008, n. 81 · art. 26",
    body: [
      "Quando il condominio affida lavori o servizi a imprese o lavoratori autonomi, deve verificarne l'idoneità tecnico-professionale e informarli sui rischi presenti. Se il condominio ha lavoratori dipendenti, nei casi previsti deve essere redatto il DUVRI, documento unico di valutazione dei rischi da interferenze.",
    ],
  },
  {
    id: "ai",
    title: "Intelligenza artificiale nelle comunicazioni",
    ref: "Reg. UE 2024/1689 (AI Act) · GDPR",
    body: [
      "Il Regolamento (UE) 2024/1689 sull'intelligenza artificiale stabilisce regole comuni per lo sviluppo e l'utilizzo dei sistemi di AI nell'Unione Europea, con obblighi graduati in base al livello di rischio e obblighi di trasparenza verso le persone.",
      "Quando l'AI elabora dati personali di condòmini si applica anche il GDPR: occorre limitare i dati a quelli necessari, informare gli interessati e mantenere il controllo umano sui testi inviati.",
    ],
  },
  {
    id: "energia",
    title: "Forniture di luce e gas del condominio",
    ref: "ARERA · Codice di condotta commerciale",
    body: [
      "Le utenze condominiali di energia elettrica e gas sono intestate al condominio e gestite dall'amministratore, che provvede al pagamento e alla ripartizione delle spese tra i condòmini secondo le tabelle millesimali o i criteri deliberati dall'assemblea.",
      "Con la fine dei servizi di tutela, la maggior parte delle forniture è passata al mercato libero. L'Autorità di Regolazione per Energia Reti e Ambiente (ARERA) stabilisce regole di trasparenza per le offerte e le bollette: confrontare le condizioni contrattuali consente al condominio di scegliere la fornitura più adatta ai propri consumi.",
    ],
  },
];

export default function Normativa() {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <SiteHeader />
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Normativa</p>
          <h1 className="fade-up mt-3 max-w-2xl font-heading text-4xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl">Le regole per il condominio, in breve.</h1>
          <p className="mt-5 max-w-xl leading-relaxed text-slate-600">Una sintesi delle principali norme su privacy, videosorveglianza, formazione, qualità dell'acqua e sicurezza. I contenuti hanno carattere informativo e non sostituiscono una consulenza professionale.</p>
        </div>
      </section>
      <main className="mx-auto grid w-full max-w-6xl flex-1 gap-10 px-6 py-14 lg:grid-cols-[220px_1fr]">
        <aside className="hidden lg:sticky lg:top-24 lg:block lg:self-start">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Argomenti</p>
          <nav className="mt-3 space-y-1 border-l border-slate-200">
            {SECTIONS.map((s) => <a key={s.id} href={`#${s.id}`} className="-ml-px block border-l-2 border-transparent py-1 pl-3 text-sm text-slate-600 hover:border-blue-600 hover:text-slate-900">{s.title}</a>)}
          </nav>
        </aside>
        <div>
          <Accordion type="multiple" defaultValue={["gdpr"]} className="rounded-2xl border border-slate-200 bg-white" data-testid="normativa-accordion">
            {SECTIONS.map((s) => (
              <AccordionItem key={s.id} value={s.id} id={s.id} className="scroll-mt-24 px-6 last:border-0">
                <AccordionTrigger data-testid={`normativa-item-${s.id}`} className="py-5 text-left hover:no-underline">
                  <span>
                    <span className="block font-heading text-lg font-semibold text-slate-900">{s.title}</span>
                    <span className="mt-1 block font-mono text-[11px] font-normal text-slate-500">{s.ref}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pb-6 text-[15px] leading-relaxed text-slate-700">
                  {s.body.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-slate-900 p-6">
            <p className="font-heading font-semibold text-white">Hai un dubbio sul tuo condominio?</p>
            <Link to="/contatti" data-testid="normativa-contatti-button" className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-medium text-slate-900 hover:bg-blue-50">Contattaci <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
